import { useEffect, useCallback, useRef } from "react";

const useIdleTimer = (onIdle, timeout, skip = false) => {
  const timeoutId = useRef(null);
  const onIdleRef = useRef(onIdle);
  const lastSavedTime = useRef(0);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const getLastActivityTime = useCallback(() => {
    const lastActivity = localStorage.getItem("last_activity_timestamp");
    const lastConsultation = localStorage.getItem("last_consultation_activity");
    const t1 = lastActivity ? parseInt(lastActivity, 10) : 0;
    const t2 = lastConsultation ? parseInt(lastConsultation, 10) : 0;
    return Math.max(t1, t2);
  }, []);

  const resetTimer = useCallback(() => {
    if (skip) return;

    const now = Date.now();
    // Throttle writing to localStorage to once every 5 seconds to avoid performance degradation
    if (now - lastSavedTime.current > 5000) {
      localStorage.setItem("last_activity_timestamp", now.toString());
      lastSavedTime.current = now;
    }

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    timeoutId.current = setTimeout(() => {
      const lastActivityTime = getLastActivityTime();
      if (lastActivityTime > 0) {
        const timePassed = Date.now() - lastActivityTime;
        if (timePassed < timeout) {
          // There was activity in another tab or consultation, reschedule for the remaining time
          const remainingTime = timeout - timePassed;
          timeoutId.current = setTimeout(
            () => onIdleRef.current(),
            Math.max(remainingTime, 1000)
          );
          return;
        }
      }
      onIdleRef.current();
    }, timeout);
  }, [timeout, skip, getLastActivityTime]);

  useEffect(() => {
    if (skip) {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      return;
    }

    // Check if session has already expired on load/tab activation
    const hasUserData = !!localStorage.getItem("userData");
    if (hasUserData) {
      const lastActivityTime = getLastActivityTime();
      if (lastActivityTime > 0) {
        const timePassed = Date.now() - lastActivityTime;
        if (timePassed >= timeout) {
          console.info("Session already expired on initialization.");
          onIdleRef.current();
          return;
        }
      } else {
        // Initialize if not present to avoid immediate timeout
        localStorage.setItem("last_activity_timestamp", Date.now().toString());
        lastSavedTime.current = Date.now();
      }
    }

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ];

    const handleEvent = () => resetTimer();

    events.forEach((e) => window.addEventListener(e, handleEvent));

    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleEvent));
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [resetTimer, skip, timeout, getLastActivityTime]);

  return { resetTimer };
};

export default useIdleTimer;
